import { fs, ink } from '@/theme/tokens';
import { ROW_ACTIONS } from '@/theme';
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel,
  type GridRowSelectionModel,
  type GridRowId,
  type GridValidRowModel,
  type DataGridProps,
} from '@mui/x-data-grid';
import { Box, Paper, Stack } from '@mui/material';
import { type ReactNode } from 'react';
import { FilterBar, type FilterDef } from '@/components/FilterBar';
import { BulkActionBar } from '@/components/BulkActionBar';
import { useColumnPinning, PinColumnsButton } from './useColumnPinning';

/**
 * Row-action icon styling applied directly here because MUI X does not
 * reliably consume the theme `MuiDataGrid` override. Same object as plain tables.
 */
const ACTION_ICON_SX = {
  '& .MuiDataGrid-cell[data-field="actions"] .MuiIconButton-root': ROW_ACTIONS,
};

/**
 * DataTable — the ONE list-table component for the app. Wraps MUI X DataGrid
 * (community) with every shared convention baked in, so each list page just
 * supplies data + config and automatically gets: a standard toolbar
 * (search + filters + pin + count), column pinning (freeze up to 4), the
 * uniform action-column styling (from the theme), server/client pagination,
 * a bulk-selection bar, sticky header, and loading/empty/error states.
 *
 * Add a feature here once → every list page gets it. Small static/config
 * tables can stay as plain <Table> (they share the global theme for looks).
 */
export type DataTableToolbar = {
  search?: { value: string; onChange: (v: string) => void; placeholder?: string;
             /** Explicit Search button instead of search-as-you-type. */
             button?: boolean };
  filters?: FilterDef[];
  values?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  onClearAll?: () => void;
  /** Page-specific controls, shown left of the pin button + count. */
  end?: ReactNode;
  /** e.g. "113 employees" */
  count?: ReactNode;
};

export type DataTableProps<R extends GridValidRowModel> = {
  rows: R[];
  columns: GridColDef<R>[];
  getRowId: (row: R) => GridRowId;
  loading?: boolean;
  error?: ReactNode;
  emptyMessage?: ReactNode;

  /** Enable column pinning. `storageKey` persists the choice per table. */
  pin?: { storageKey: string; defaultPinned?: string[]; max?: number };

  toolbar?: DataTableToolbar;
  bulk?: { count: number; onClear: () => void; actions: ReactNode };

  paginationMode?: 'server' | 'client';
  /**
   * Server sorting. Omit for the default client sort, which is correct only
   * when the grid holds every row — with server pagination it would reorder
   * the current page and silently claim to have sorted the whole table.
   */
  sortingMode?: 'server' | 'client';
  sortModel?: GridSortModel;
  onSortModelChange?: (m: GridSortModel) => void;
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (m: GridPaginationModel) => void;
  pageSizeOptions?: number[];
  /** Show the total + "showing x–y" note in the footer (server pagination). */
  footerTotalLabel?: (total: number, model: GridPaginationModel) => ReactNode;

  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (m: GridRowSelectionModel) => void;

  onRowClick?: DataGridProps<R>['onRowClick'];
  onRowDoubleClick?: DataGridProps<R>['onRowDoubleClick'];
  /** `() => 'auto'` lets rows grow to fit text that wraps. */
  getRowHeight?: DataGridProps<R>['getRowHeight'];
  autoHeight?: boolean;
  /**
   * When set (e.g. "calc(100vh - 330px)"), the grid takes this fixed height and
   * scrolls its rows INTERNALLY, so the column header (and footer) stay pinned
   * while you scroll. Omit for the default auto-height (page scrolls, header
   * scrolls away with it).
   */
  viewportHeight?: string | number;
  /**
   * Fill the remaining height of the page card (flexbox), so the grid is as
   * tall as the screen allows on ANY laptop — no hardcoded pixel height — with
   * a pinned header + internal scroll. Takes precedence over viewportHeight.
   *
   * The page's root must be a flex column with a definite height. Use a
   * `max()` FLOOR so the table can't collapse on small screens (it keeps a
   * usable min height and the page scrolls instead):
   *   sx={{ display:'flex', flexDirection:'column',
   *         height:'max(560px, calc(100vh - 116px))' }}
   */
  fillHeight?: boolean;
  sx?: DataGridProps<R>['sx'];
  slots?: DataGridProps<R>['slots'];
};

export function DataTable<R extends GridValidRowModel>({
  rows, columns, getRowId, loading, error, emptyMessage,
  pin, toolbar, bulk,
  paginationMode = 'server', rowCount, paginationModel, onPaginationModelChange,
  sortingMode, sortModel, onSortModelChange,
  // The MIT DataGrid refuses a page size over 100.
  pageSizeOptions = [10, 25, 50, 100], footerTotalLabel,
  checkboxSelection, rowSelectionModel, onRowSelectionModelChange,
  onRowClick, onRowDoubleClick, getRowHeight, autoHeight = true, viewportHeight, fillHeight, sx, slots,
}: DataTableProps<R>) {
  // A long page of rows scrolls inside the grid, so the column header stays pinned.
  const longList = !fillHeight && !viewportHeight && autoHeight && rows.length > 12;
  const height = viewportHeight ?? (longList ? 'max(420px, calc(100vh - 240px))' : undefined);
  const useAutoHeight = (fillHeight || height) ? false : autoHeight;
  // Always call the hook (Rules of Hooks); a blank storageKey just means "off".
  const pinning = useColumnPinning<R>({
    storageKey: pin?.storageKey ?? '__off__',
    columns,
    defaultPinned: pin?.defaultPinned ?? [],
    max: pin?.max ?? 4,
  });
  const pinnable = pin
    ? columns
        .filter((c) => c.field !== 'actions' && typeof c.headerName === 'string' && c.headerName)
        .map((c) => ({ field: c.field, label: c.headerName as string }))
    : [];

  const clickable = !!(onRowClick || onRowDoubleClick);

  const mergedSlots: DataGridProps<R>['slots'] = {
    ...(emptyMessage
      ? { noRowsOverlay: () => (
          <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', p: 4, color: 'text.secondary', fontSize: fs.base }}>
            {emptyMessage}
          </Stack>
        ) }
      : {}),
    ...(footerTotalLabel
      ? { footer: () => (
          <GridFooterContainer>
            <Box sx={{ pl: 2, fontSize: fs.base, fontWeight: 600 }}>
              {footerTotalLabel(rowCount ?? rows.length, paginationModel ?? { page: 0, pageSize: 0 })}
            </Box>
            <GridPagination />
          </GridFooterContainer>
        ) }
      : {}),
    ...slots,
  };

  return (
    <>
      {toolbar && (
        <FilterBar
          searchPlaceholder={toolbar.search?.placeholder}
          searchButton={toolbar.search?.button}
          searchValue={toolbar.search?.value ?? ''}
          onSearchChange={toolbar.search?.onChange ?? (() => {})}
          filters={toolbar.filters ?? []}
          values={toolbar.values ?? {}}
          onFilterChange={toolbar.onFilterChange ?? (() => {})}
          onClearAll={toolbar.onClearAll ?? (() => {})}
          rightSlot={
            (toolbar.end || pin || toolbar.count) ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                {toolbar.end}
                {pin && <PinColumnsButton {...pinning.buttonProps(pinnable)} />}
                {toolbar.count}
              </Stack>
            ) : undefined
          }
        />
      )}

      {/* Grid-only pages (their own toolbar) still get the pin button — in a
          right-aligned strip above the grid. */}
      {!toolbar && pin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <PinColumnsButton {...pinning.buttonProps(pinnable)} />
        </Box>
      )}

      {bulk && <BulkActionBar count={bulk.count} onClear={bulk.onClear} actions={bulk.actions} />}

      <Paper sx={{
        border: 1, borderColor: 'divider', width: '100%', minWidth: 0,
        ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
      }}>
        <Box
          ref={pin ? pinning.gridRef : undefined}
          sx={fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
        >
          <DataGrid
            rows={rows}
            columns={pin ? pinning.orderedColumns : columns}
            getRowId={getRowId}
            loading={loading}
            autoHeight={useAutoHeight}
            paginationMode={paginationMode}
            rowCount={paginationMode === 'server' ? rowCount : undefined}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortingMode={sortingMode}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            pageSizeOptions={pageSizeOptions}
            checkboxSelection={checkboxSelection}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            getRowHeight={getRowHeight}
            disableRowSelectionOnClick
            slots={mergedSlots}
            {...(pin ? pinning.gridProps : {})}
            sx={[
              {
                border: 0,
                '& .MuiDataGrid-row:hover': { cursor: clickable ? 'pointer' : 'default' },
                // Vertically centre EVERY cell's content — renderCell columns
                // (multi-line name, action buttons) default to top-aligned block
                // cells, so without this they sit higher than the plain cells.
                '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', borderColor: ink.line, fontSize: 13.5 },
                // No square focus outline when a cell/header is clicked — it
                // reads as a stray black box, not a selection.
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                // Match the plain-table header band (grey, small uppercase).
                // Pinned headers read the same CSS var, so the row stays uniform.
                '--DataGrid-containerBackground': ink.headBg,
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700, color: ink.headFg, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.3px' },
                '& .MuiDataGrid-columnHeaders': { borderBottom: `1px solid ${ink.border}` },
              },
              ACTION_ICON_SX,
              ...(fillHeight ? [{ flex: 1, minHeight: 0 }] : []),
              ...(!fillHeight && height ? [{ height }] : []),
              ...(pin ? [pinning.pinnedSx] : []),
              ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
            ]}
          />
        </Box>
        {error && <Box sx={{ p: 2, color: 'error.main', fontSize: fs.base }}>{error}</Box>}
      </Paper>
    </>
  );
}
